import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, CheckCircle, XCircle, AlertCircle, Plus, 
  ArrowLeft, Search, User, Calendar, Download,
  Pen, Clock, Shield
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FORM_TYPE_COLORS = {
  medical: 'red',
  photo_video: 'blue',
  training: 'green',
  data_processing: 'purple',
  transport: 'orange'
};

const FORM_TYPE_ICONS = {
  medical: '🏥',
  photo_video: '📸',
  training: '🏃',
  data_processing: '🔒',
  transport: '🚗'
};

export default function ConsentForms() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [children, setChildren] = useState([]);
  const [formTypes, setFormTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [athleteSearch, setAthleteSearch] = useState('');
  const [showAthleteDropdown, setShowAthleteDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    child_id: '',
    form_type: 'training',
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    guardian_relationship: 'Parent',
    additional_data: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [formsRes, childrenRes, typesRes] = await Promise.all([
        axios.get(`${API}/consent-forms`),
        axios.get(`${API}/children`),
        axios.get(`${API}/consent-form-types`)
      ]);
      setForms(formsRes.data.consent_forms);
      setChildren(childrenRes.data);
      setFormTypes(typesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  useEffect(() => {
    if (showModal && signatureType === 'drawn') {
      setTimeout(initCanvas, 100);
    }
  }, [showModal, signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
  };

  const getSignatureData = () => {
    if (signatureType === 'typed') {
      return typedSignature;
    }
    const canvas = canvasRef.current;
    return canvas ? canvas.toDataURL('image/png') : '';
  };

  const downloadPDF = async (formId, childName, formType, childId) => {
    try {
      const token = localStorage.getItem('token');
      
      // First, save the consent form to documents
      const saveResponse = await axios.post(`${API}/consent-forms/${formId}/pdf/save`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (saveResponse.data?.document) {
        toast.success('Consent form saved to Media tab!');
        
        // Download the saved file
        const downloadResponse = await axios.get(`${API}/children/${childId}/documents/${saveResponse.data.document.id}/download`, {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = saveResponse.data.document.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      // Fallback to direct download
      try {
        const response = await axios.get(`${API}/consent-forms/${formId}/pdf`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `consent_${childName.replace(' ', '_')}_${formType}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF downloaded successfully');
      } catch (fallbackError) {
        toast.error('Failed to download PDF');
      }
    }
  };

  const createConsentForm = async (e) => {
    e.preventDefault();
    
    const signatureData = getSignatureData();
    if (!signatureData || (signatureType === 'typed' && !typedSignature.trim())) {
      toast.error('Please provide a signature');
      return;
    }
    
    try {
      await axios.post(`${API}/consent-forms`, {
        ...formData,
        signature_data: signatureData,
        signature_type: signatureType,
        agreed_terms: true
      });
      
      toast.success('Consent form signed successfully');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create consent form');
    }
  };

  const revokeForm = async (formId) => {
    if (!window.confirm('Are you sure you want to revoke this consent? This action cannot be undone.')) return;
    try {
      await axios.put(`${API}/consent-forms/${formId}/revoke`);
      toast.success('Consent revoked');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke consent');
    }
  };

  const resetForm = () => {
    setFormData({
      child_id: '',
      form_type: 'training',
      guardian_name: '',
      guardian_email: '',
      guardian_phone: '',
      guardian_relationship: 'Parent',
      additional_data: {}
    });
    setTypedSignature('');
    setSignatureType('drawn');
    setAthleteSearch('');
    setShowAthleteDropdown(false);
  };

  const filteredForms = forms.filter(f => {
    const matchesStatus = statusFilter === 'all' || f.status === statusFilter;
    const matchesType = typeFilter === 'all' || f.form_type === typeFilter;
    const matchesSearch = !searchTerm || 
      f.child_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.guardian_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });

  const activeCount = forms.filter(f => f.status === 'active').length;
  const revokedCount = forms.filter(f => f.status === 'revoked').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Loading consent forms...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="consent-forms-page">
      {/* Header */}
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <FileText className="h-8 w-8 text-[#02b5ff]" />
                  Consent Forms
                </h1>
                <p className="text-gray-400 mt-1">Manage digital consent forms for athletes</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowModal(true)}
                className="h-12 px-6 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold rounded-full shadow-lg"
              >
                <Plus className="mr-2 h-5 w-5" />
                New Consent Form
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-white">{forms.length}</div>
            <div className="text-sm text-gray-400">Total Forms</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#02b5ff]">{activeCount}</div>
            <div className="text-sm text-gray-400">Active</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#ff025f]">{revokedCount}</div>
            <div className="text-sm text-gray-400">Revoked</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#02b5ff]">{children.length}</div>
            <div className="text-sm text-gray-400">Athletes</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by athlete or guardian name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 rounded-xl text-white placeholder:text-gray-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 rounded-xl text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 rounded-xl text-white">
              <SelectValue placeholder="Form Type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(formTypes).map(([key, value]) => (
                <SelectItem key={key} value={key}>{value.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Forms List */}
        {filteredForms.length === 0 ? (
          <div className="bg-zinc-900 rounded-2xl p-12 shadow-md border border-zinc-800 text-center">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Consent Forms Found</h3>
            <p className="text-gray-500">Create a new consent form to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredForms.map((form) => (
              <div 
                key={form.id}
                className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{FORM_TYPE_ICONS[form.form_type] || '📄'}</span>
                    <div>
                      <div className="font-semibold text-white text-sm">{form.form_name}</div>
                      <div className="text-xs text-gray-500 capitalize">{form.form_type.replace('_', ' ')}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    form.status === 'active' 
                      ? 'bg-[#02b5ff]/10 text-[#02b5ff]' 
                      : 'bg-[#ff025f]/10 text-[#ff025f]'
                  }`}>
                    {form.status === 'active' ? <CheckCircle className="inline h-3 w-3 mr-1" /> : <XCircle className="inline h-3 w-3 mr-1" />}
                    {form.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-white">{form.child_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Pen className="h-4 w-4 text-gray-400" />
                    <span>Signed by: {form.guardian_name} ({form.guardian_relationship})</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>Signed: {new Date(form.signed_at).toLocaleDateString()}</span>
                  </div>
                  {form.expires_at && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>Expires: {new Date(form.expires_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Signature Preview */}
                {form.signature_type === 'drawn' && form.signature_data?.startsWith('data:image') && (
                  <div className="mb-4 p-2 bg-zinc-800 rounded-lg">
                    <img 
                      src={form.signature_data} 
                      alt="Signature" 
                      className="h-12 mx-auto object-contain"
                    />
                  </div>
                )}
                {form.signature_type === 'typed' && (
                  <div className="mb-4 p-2 bg-zinc-800 rounded-lg text-center">
                    <span className="font-signature text-xl text-[#02b5ff] italic">{form.signature_data}</span>
                  </div>
                )}

                <div className="flex gap-2">
                  {form.status === 'active' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPDF(form.id, form.child_name, form.form_type, form.child_id)}
                        className="flex-1 text-[#02b5ff] border-indigo-200 hover:bg-indigo-50 rounded-xl"
                        data-testid={`download-pdf-${form.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revokeForm(form.id)}
                        className="flex-1 text-[#ff025f] border-[#ff025f]/30 hover:bg-[#ff025f]/10 rounded-xl"
                        data-testid={`revoke-consent-${form.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Revoke
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Consent Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-zinc-900 rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 text-[#02b5ff]" />
              <div>
                <h2 className="text-2xl font-bold text-white">Sign Consent Form</h2>
                <p className="text-sm text-gray-500">Digital signature required</p>
              </div>
            </div>
            
            <form onSubmit={createConsentForm} className="space-y-5">
              {/* Athlete Selection - Searchable */}
              <div className="relative">
                <Label className="text-sm font-semibold text-gray-300">Athlete</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                  <Input
                    placeholder="Search athlete by name..."
                    value={athleteSearch}
                    onChange={(e) => {
                      setAthleteSearch(e.target.value);
                      setShowAthleteDropdown(true);
                    }}
                    onFocus={() => setShowAthleteDropdown(true)}
                    className="pl-10 bg-zinc-800 border-zinc-700 rounded-xl text-white"
                  />
                  {formData.child_id && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, child_id: ''});
                          setAthleteSearch('');
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                {showAthleteDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {children
                      .filter(child => 
                        `${child.first_name} ${child.last_name}`.toLowerCase().includes(athleteSearch.toLowerCase())
                      )
                      .slice(0, 50)
                      .map(child => (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => {
                            setFormData({...formData, child_id: child.id});
                            setAthleteSearch(`${child.first_name} ${child.last_name}`);
                            setShowAthleteDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-zinc-700 flex items-center gap-2 ${
                            formData.child_id === child.id ? 'bg-zinc-700 text-[#02b5ff]' : 'text-white'
                          }`}
                        >
                          <User className="h-4 w-4 text-gray-400" />
                          {child.first_name} {child.last_name}
                        </button>
                      ))
                    }
                    {children.filter(child => 
                      `${child.first_name} ${child.last_name}`.toLowerCase().includes(athleteSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="px-4 py-3 text-gray-400 text-sm">No athletes found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Type */}
              <div>
                <Label className="text-sm font-semibold text-gray-300">Consent Type</Label>
                <Select value={formData.form_type} onValueChange={(v) => setFormData({...formData, form_type: v})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Object.entries(formTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {FORM_TYPE_ICONS[key]} {value.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formTypes[formData.form_type] && (
                  <p className="text-xs text-gray-500 mt-2 p-3 bg-zinc-800 rounded-lg">
                    {formTypes[formData.form_type].description}
                  </p>
                )}
              </div>

              {/* Guardian Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Guardian Name *</Label>
                  <Input
                    value={formData.guardian_name}
                    onChange={(e) => setFormData({...formData, guardian_name: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl text-white"
                    placeholder="Full legal name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Relationship</Label>
                  <Select value={formData.guardian_relationship} onValueChange={(v) => setFormData({...formData, guardian_relationship: v})}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="Parent">Parent</SelectItem>
                      <SelectItem value="Guardian">Legal Guardian</SelectItem>
                      <SelectItem value="Carer">Carer</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.guardian_email}
                    onChange={(e) => setFormData({...formData, guardian_email: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="guardian@email.com"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Phone</Label>
                  <Input
                    value={formData.guardian_phone}
                    onChange={(e) => setFormData({...formData, guardian_phone: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    placeholder="+44 7xxx xxx xxx"
                  />
                </div>
              </div>

              {/* Medical Form Additional Fields */}
              {formData.form_type === 'medical' && (
                <div className="p-4 bg-[#ff025f]/10 rounded-xl border border-[#ff025f]/30 space-y-4">
                  <h4 className="font-semibold text-[#ff025f]">Medical Information</h4>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Emergency Contact Name</Label>
                    <Input
                      value={formData.additional_data.emergency_contact_name || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, emergency_contact_name: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Emergency Contact Phone</Label>
                    <Input
                      value={formData.additional_data.emergency_contact_phone || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, emergency_contact_phone: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Known Medical Conditions</Label>
                    <Input
                      value={formData.additional_data.medical_conditions || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, medical_conditions: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                      placeholder="List any conditions..."
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#ff025f]">Allergies</Label>
                    <Input
                      value={formData.additional_data.allergies || ''}
                      onChange={(e) => setFormData({
                        ...formData, 
                        additional_data: {...formData.additional_data, allergies: e.target.value}
                      })}
                      className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                      placeholder="List any allergies..."
                    />
                  </div>
                </div>
              )}

              {/* Signature */}
              <div>
                <Label className="text-sm font-semibold text-gray-300">Digital Signature *</Label>
                <div className="flex gap-2 mt-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={signatureType === 'drawn' ? 'default' : 'outline'}
                    onClick={() => setSignatureType('drawn')}
                    className="rounded-full"
                  >
                    <Pen className="h-4 w-4 mr-1" />
                    Draw
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={signatureType === 'typed' ? 'default' : 'outline'}
                    onClick={() => setSignatureType('typed')}
                    className="rounded-full"
                  >
                    Type
                  </Button>
                </div>

                {signatureType === 'drawn' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 bg-zinc-800">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      className="w-full bg-zinc-900 rounded-lg cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={clearSignature}
                      className="mt-2 text-gray-500"
                    >
                      Clear Signature
                    </Button>
                  </div>
                ) : (
                  <Input
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 rounded-xl text-xl font-signature italic text-[#02b5ff]"
                    placeholder="Type your full name"
                  />
                )}
              </div>

              {/* Terms */}
              <div className="p-4 bg-zinc-800 rounded-xl text-sm text-gray-400">
                <p>By signing this form, I confirm that:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>I am the parent/legal guardian of the athlete named above</li>
                  <li>All information provided is accurate to the best of my knowledge</li>
                  <li>I have read and understood the terms of this consent</li>
                  <li>This digital signature is legally binding</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.child_id || !formData.guardian_name}
                  className="flex-1 bg-[#02b5ff] hover:bg-indigo-700 text-white rounded-xl"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Sign & Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx global>{`
        .font-signature {
          font-family: 'Brush Script MT', 'Segoe Script', cursive;
        }
      `}</style>
    </div>
  );
}
